import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { submitLead, errorMessage } from "../lib/api";

export const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="field-label">{label}</span>
    {children}
  </label>
);

export const useLeadForm = (initial) => {
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const on = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const send = async (payload, onOk) => {
    setBusy(true);
    try {
      await submitLead(payload);
      onOk?.();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return { v, setV, on, busy, send };
};

export const SubmitButton = ({ busy, children, testId }) => (
  <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60 sm:w-auto" data-testid={testId}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
    {children} {!busy && <ArrowRight className="h-4 w-4" />}
  </button>
);

export const SuccessPanel = ({ title, line, testId }) => (
  <div className="rounded-md border border-slate-200 bg-white p-8" data-testid={testId}>
    <CheckCircle2 className="h-10 w-10 text-flame-600" />
    <h3 className="mt-4 text-2xl font-extrabold text-navy">{title}</h3>
    <p className="mt-2 text-slate-600">{line}</p>
  </div>
);

const VERTICALS = ["Offices", "Hospitality", "Retail", "High-Rise & Multi-Unit", "Property Managers", "Other"];

export const WalkthroughForm = ({ vertical = "" }) => {
  const [done, setDone] = useState(false);
  const { v, on, busy, send } = useLeadForm({ name: "", company: "", phone: "", email: "", location: "", project_type: vertical, message: "" });
  if (done) return <SuccessPanel testId="walkthrough-success" title="Request received." line="A project lead will connect with you within 24 hours to book your site walkthrough." />;
  const submit = (e) => { e.preventDefault(); send({ type: "walkthrough", ...v }, () => setDone(true)); };
  return (
    <form onSubmit={submit} className="grid gap-4 rounded-md border border-slate-200 bg-white p-6 sm:grid-cols-2 sm:p-8" data-testid="walkthrough-form">
      <Field label="Your name" id="wt-name"><input id="wt-name" required className="field" value={v.name} onChange={on("name")} data-testid="walkthrough-name-input" /></Field>
      <Field label="Company" id="wt-company"><input id="wt-company" className="field" value={v.company} onChange={on("company")} data-testid="walkthrough-company-input" /></Field>
      <Field label="Phone" id="wt-phone"><input id="wt-phone" type="tel" required className="field" value={v.phone} onChange={on("phone")} data-testid="walkthrough-phone-input" /></Field>
      <Field label="Email" id="wt-email"><input id="wt-email" type="email" required className="field" value={v.email} onChange={on("email")} data-testid="walkthrough-email-input" /></Field>
      <Field label="Property city" id="wt-loc"><input id="wt-loc" className="field" value={v.location} onChange={on("location")} placeholder="e.g. North York" data-testid="walkthrough-location-input" /></Field>
      <Field label="Property type" id="wt-type">
        <select id="wt-type" className="field" value={v.project_type} onChange={on("project_type")} data-testid="walkthrough-vertical-select">
          <option value="">Select one</option>
          {VERTICALS.map((x) => <option key={x}>{x}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="What needs painting?" id="wt-msg"><textarea id="wt-msg" rows={4} className="field" value={v.message} onChange={on("message")} placeholder="Floors, square footage, timing, after-hours needs…" data-testid="walkthrough-message-input" /></Field>
      </div>
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600">A project lead will connect with you within 24 hours.</p>
        <SubmitButton busy={busy} testId="walkthrough-form-submit-btn">Request a Site Walkthrough</SubmitButton>
      </div>
    </form>
  );
};

export const ContactForm = () => {
  const [done, setDone] = useState(false);
  const { v, on, busy, send } = useLeadForm({ name: "", phone: "", email: "", message: "" });
  if (done) return <SuccessPanel testId="contact-success" title="Message sent." line="Ilias will get back to you shortly. If it's urgent, call (647) 985-8007." />;
  const submit = (e) => { e.preventDefault(); send({ type: "contact", ...v }, () => setDone(true)); };
  return (
    <form onSubmit={submit} className="grid gap-4 rounded-md border border-slate-200 bg-white p-6 sm:grid-cols-2 sm:p-8" data-testid="contact-form">
      <Field label="Your name" id="c-name"><input id="c-name" required className="field" value={v.name} onChange={on("name")} data-testid="contact-name-input" /></Field>
      <Field label="Phone" id="c-phone"><input id="c-phone" type="tel" required className="field" value={v.phone} onChange={on("phone")} data-testid="contact-phone-input" /></Field>
      <div className="sm:col-span-2"><Field label="Email" id="c-email"><input id="c-email" type="email" required className="field" value={v.email} onChange={on("email")} data-testid="contact-email-input" /></Field></div>
      <div className="sm:col-span-2"><Field label="How can we help?" id="c-msg"><textarea id="c-msg" rows={5} required className="field" value={v.message} onChange={on("message")} data-testid="contact-message-input" /></Field></div>
      <div className="sm:col-span-2"><SubmitButton busy={busy} testId="contact-form-submit-btn">Send message</SubmitButton></div>
    </form>
  );
};
